import React, { useContext, useState, useEffect, useRef } from 'react';
import { Redirect } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import { Environment } from 'src/Environment';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { DateTime } from 'luxon';
import * as Sentry from '@sentry/react';
import Slider from 'src/components/slider/slider';
import {
  businessCashAdvanceDashboardRoute,
  businessCashAdvanceDigitalTermsRoute,
  businessCashAdvanceRoute,
  paymentsDashboardRoute,
} from 'src/routing/routes';
import { submitBCAOffer, acceptBCAOffer, getBCAOffers } from 'src/services/bca-offers.service';
import { Offer, OfferContext } from 'src/context/offer-context';
import { formatCurrencyAmountSimple } from 'src/helpers/formatting';
import useDebounce from 'src/hooks/use-debounce';
import AcceptContractModal from './accept-contract-modal/accept-contract-modal';
import AdditionalFieldsModal, {
  AdditionalFieldsValues,
} from './additional-fields-modal/additional-fields-modal';
import TealiumEvents from 'src/helpers/tealium-events';
import { getContractPdfBase64 } from 'src/helpers/contract';
import { StaticLoader } from 'src/components';
import HeaderNav from '../../header-nav/header-nav';
import Card from 'src/components/card/card';
import ApprovedModal from './approved-modal/approved-modal';
import { IMerchantDetails } from 'src/common/types';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import useMerchantProfile from 'src/hooks/useMerchantProfile';
import { SubmitModal } from './submit-modal/submit-modal';
import { Delay, controlledTimeout } from 'src/helpers/delay';
import { SENTRY_TAG_KEYS } from 'src/types';
import './business-cash-advance-calculation.scss';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

export enum BCASubmitStatus {
  NONE = 'NONE',
  COLLECTING_INFO = 'COLLECTING_INFO',
  PREPARING_APPLICATION = 'PREPARING_APPLICATION',
  SUBMITTING_APPLICATION = 'SUBMITTING_APPLICATION',
  SUCCESS = 'SUCCESS',
  TAKING_LONG = 'TAKING_LONG',
}

export default function BusinessCashAdvanceCalculation() {
  const { merchant, setMerchantDetails } = useMerchantProfile();
  const {
    offers,
    selectedOffer,
    setChosenOffer,
    setSelectedOffer,
    additionalFieldValues,
    setAdditionalFieldValues,
    setOffers,
  } = useContext(OfferContext);
  const [redirect, setRedirect] = useState<string>();
  const [amountOptions, setAmountOptions] = useState<string[] | []>([]);
  const [fundedAmountIndex, setFundedAmountIndex] = useState<number>(0);
  const [paymentPeriodIndex, setPaymentPeriodIndex] = useState<number>(0);
  const [periodOptions, setPeriodOptions] = useState<number[]>([]);
  const [currentOffer, setCurrentOffer] = useState<Offer>();
  const [offerLoading, setOfferLoading] = useState<boolean>(false);
  const [bcaSubmitStatus, setBcaSubmitStatus] = useState<BCASubmitStatus>(BCASubmitStatus.NONE);

  const debouncedFundedIndex = useDebounce(fundedAmountIndex, 1000);
  const debouncedPaymentPeriod = useDebounce(paymentPeriodIndex, 1000);
  const [offerAcceptLoading, setOfferAcceptLoading] = useState<boolean>(false);

  const [showAdditionalFieldsModal, setShowAdditionalFieldsModal] = useState<boolean>(false);
  const [showContractModal, setShowContractModal] = useState<boolean>(false);
  const [showApprovedModal, setShowApprovedModal] = useState<boolean>(false);

  const topOfPage = useRef<HTMLHeadingElement>(null);
  const BCATooltip = useRef<ReactTooltip>(null);

  const offerAvailable = () => {
    if (!currentOffer) return false;
    return (
      Object.keys(currentOffer).length > 0 &&
      currentOffer?.Amount.toString() === amountOptions[fundedAmountIndex]
    );
  };

  const updateOffer = async () => {
    const abortController = new AbortController();
    const response = await getBCAOffers(
      abortController,
      amountOptions[fundedAmountIndex],
      periodOptions[paymentPeriodIndex],
    );

    if (!response.ok) {
      toastMessageServiceSingleton.addError(response?.message, null, '', false, null, true);
      setOfferLoading(false);
      return;
    }
    const result = response.result;
    if (result?.offerDetails?.offer) {
      setCurrentOffer(result.offerDetails.offer);
      setSelectedOffer(result.offerDetails.offer);
    }
    setOfferLoading(false);
  };

  const submitAndAcceptOffer = async () => {
    const timeoutManager = controlledTimeout(() => {
      setBcaSubmitStatus(BCASubmitStatus.TAKING_LONG);
    }, 10000);
    setOfferAcceptLoading(true);
    setShowContractModal(false);
    setBcaSubmitStatus(BCASubmitStatus.COLLECTING_INFO);

    if (!currentOffer || !additionalFieldValues) {
      setOfferAcceptLoading(false);
      return;
    }

    const abortController = new AbortController();
    const submitResult = await submitBCAOffer(
      abortController,
      Number(amountOptions[fundedAmountIndex]),
      currentOffer.OfferId,
      additionalFieldValues,
    );

    if (!submitResult.ok) {
      setOfferAcceptLoading(false);
      setShowContractModal(false);
      toastMessageServiceSingleton.addError(
        submitResult.result?.Message ?? submitResult.message,
        null,
        '',
        false,
        null,
        true,
      );
      Sentry.captureException(
        `BCA: Submit offer call failed: ${merchant.merchantId}: ${JSON.stringify(submitResult)}`,
      );
      Sentry.setTag(SENTRY_TAG_KEYS.JOURNEY_NAME, 'BCA');
      return;
    }

    const result = submitResult.result;
    const address = ['line1', 'line2', 'suburb', 'city', 'province', 'country', 'postcode']
      .map((addressItem) => result.address?.[addressItem])
      .filter((addressValue) => addressValue)
      .join(', ');

    const merchantOfferDetails: IMerchantDetails = {
      merchantName: result.merchantName,
      nameOfOwner: `${additionalFieldValues?.name} ${additionalFieldValues?.surname}`,
      idNumber: additionalFieldValues?.id,
      advanceStart: DateTime.fromJSDate(new Date()).toFormat('dd/LL/yyyy'),
      address: address,
      vatNumber: result.vatRegistrationNumber,
      advanceAmount: amountOptions[fundedAmountIndex],
      totalRepayment: currentOffer?.TotalRepayment,
      repaymentRate: currentOffer?.Rate,
    };

    setMerchantDetails(merchantOfferDetails);
    setBcaSubmitStatus(BCASubmitStatus.PREPARING_APPLICATION);
    timeoutManager.restart();
    await Delay(2000);

    const contractBase64 = await getContractPdfBase64(
      merchantOfferDetails,
      merchant,
      selectedOffer,
    );

    const params = {
      amount: amountOptions[fundedAmountIndex],
      applicationId: result.ApplicationId,
      accountHolderName: result.accountHolderName,
      branchCode: result.branchCode,
      fileBase64: contractBase64,
      bankName: result.bankName,
      accountNumber: result.accountNumber,
      merchantName: result.merchantName,
      merchantEmailId: result.merchantEmailId,
      bankAccountType: result.bankAccountType,
    };

    setBcaSubmitStatus(BCASubmitStatus.SUBMITTING_APPLICATION);
    timeoutManager.restart();
    await Delay(1000);

    const resultAccept = await acceptBCAOffer(params);

    if (!resultAccept.ok) {
      toastMessageServiceSingleton.addError(resultAccept?.message, null, '', false, null, true);
      setOfferAcceptLoading(false);
      Sentry.captureException(
        `BCA: accept terms call failed: ${merchant.merchantId} : ${JSON.stringify(resultAccept)}`,
      );
      Sentry.setTag(SENTRY_TAG_KEYS.JOURNEY_NAME, 'BCA');
      return;
    }

    setChosenOffer(currentOffer ?? {});
    setShowContractModal(false);
    setShowApprovedModal(true);
    setOffers(null);

    setOfferAcceptLoading(false);
    setBcaSubmitStatus(BCASubmitStatus.SUCCESS);
    timeoutManager.clear();
  };

  const updatePeriodSlider = (currentPeriod, amount = amountOptions[fundedAmountIndex]) => {
    if (!offers) return;
    let terms: number[] = offers?.amountTermsMap[amount].sort((a, b) => a - b);
    setPeriodOptions(terms);

    let index = terms.findIndex((term) => currentPeriod === term);
    setPaymentPeriodIndex(index !== -1 ? index : 0);
  };

  useEffect(() => {
    if (topOfPage.current) {
      topOfPage.current.scrollIntoView(false);
    }
  }, []);

  useEffect(() => {
    const getBCAOfferOptions = async () => {
      if (!!offers && Object.keys(offers).length > 0) {
        let amounts = Object.keys(offers.amountTermsMap).sort((a, b) => Number(a) - Number(b));
        setAmountOptions(amounts);
        let i = amounts.findIndex((amount) =>
          selectedOffer !== currentOffer
            ? selectedOffer.Amount === parseInt(amount)
            : offers?.offerDetails?.offer?.Amount === parseInt(amount),
        );
        i = i !== -1 ? i : 0;
        updatePeriodSlider(offers.offerDetails?.offer?.Term, amounts[i]);

        setFundedAmountIndex(i);
        setCurrentOffer(offers.offerDetails.offer);
        setSelectedOffer(offers.offerDetails.offer);
      } else {
        return <Redirect push to={businessCashAdvanceRoute} />;
      }
    };
    getBCAOfferOptions();
  }, [offers]);

  useEffect(() => {
    if (amountOptions.length > 0) {
      updateOffer();
    }
  }, [debouncedFundedIndex, debouncedPaymentPeriod]);

  useEffect(() => {
    TealiumEvents.applyNowBCAPageLoad();
  }, []);

  const tooltipContent = () => (
    <>
      <span className="is-icon-6 icon-cross is-pulled-right"></span>
      <div className="tooltip-text">
        <p>
          The percentage selected will be what we deduct from your daily turnover to repay your
          advance. This percentage deduction will remain the same until your advance is fully paid
          off. This is automatically completed before your daily settlement into your business
          account.
        </p>
        <br />
        <p>Remember, when you don't transact, you don't pay.</p>
      </div>
    </>
  );

  // TODO: BCA Flag to be removed
  if (Environment.current?.disabledFeatures.bca) {
    return <Redirect push to={paymentsDashboardRoute} />;
  }

  if (redirect) {
    return <Redirect push to={redirect} />;
  }

  return (
    <div className="BCACalculation">
      <HeaderNav
        title={merchant?.companyName ? `Hi, welcome back ${merchant.companyName}` : ''}
        screenName="Business Cash Advance"
      >
        <AdditionalFieldsModal
          show={showAdditionalFieldsModal}
          onHide={() => setShowAdditionalFieldsModal(false)}
          onNext={(values: AdditionalFieldsValues) => {
            setAdditionalFieldValues(values);
            setShowAdditionalFieldsModal(false);
            setShowContractModal(true);
          }}
        />
        <AcceptContractModal
          show={showContractModal}
          onHide={() => setShowContractModal(false)}
          onAcceptOffer={() => {
            TealiumEvents.confirmTCsBCAClickConfirm();
            submitAndAcceptOffer();
          }}
          currentOffer={currentOffer}
          disabled={offerAcceptLoading}
        />
        <ApprovedModal
          show={showApprovedModal}
          onHide={() => {
            setShowApprovedModal(false);
            setRedirect(businessCashAdvanceDashboardRoute);
          }}
          onApplicationSuccessful={() => {
            const amount = currentOffer?.Amount || -1;
            const total = currentOffer?.TotalRepayment || -1;
            const term = currentOffer?.Term || -1;
            const id = currentOffer?.OfferId || 'unknown';

            TealiumEvents.bcaSuccessful(
              amount.toString(),
              total.toString(),
              term.toString(),
              id.substring(0, 19),
            );
          }}
          onRedirectContract={() => setRedirect(businessCashAdvanceDigitalTermsRoute)}
        />
        <SubmitModal status={bcaSubmitStatus} show={offerAcceptLoading} />
        <h1
          className="page-title has-margin-bottom-4 is-hidden-mobile"
          automationid="Business_Cash_Advance_Calculation_H1_Page_Heading"
          ref={topOfPage}
        >
          Business Cash Advance
        </h1>
        <Card cardTitle="Funding information">
          <div className="rounded-container">
            <h3 className="box-title is-size-6 has-padding-top-7 has-margin-bottom-6">
              Funding amount
            </h3>
            <div className="columns is-mobile">
              <div className="column is-narrow">
                <div
                  className="slider-down is-clickable"
                  onClick={() => {
                    if (fundedAmountIndex > 0) {
                      const newFundedAmountIndex = fundedAmountIndex - 1;
                      setOfferLoading(true);
                      setFundedAmountIndex(newFundedAmountIndex);
                      updatePeriodSlider(
                        periodOptions[paymentPeriodIndex],
                        amountOptions[newFundedAmountIndex],
                      );
                    }
                  }}
                >
                  <span className="is-icon-5 icon-minus"></span>
                </div>
              </div>
              <div className="column">
                <Slider
                  disabled={amountOptions.length <= 1}
                  xMax={amountOptions.length - 1 || 0}
                  x={fundedAmountIndex}
                  isMobile={false}
                  onChange={({ x }) => {
                    setOfferLoading(true);
                    setFundedAmountIndex(x);
                    updatePeriodSlider(periodOptions[paymentPeriodIndex], amountOptions[x]);
                  }}
                  xLabelFormatter={(x) =>
                    `R ${amountOptions[x]?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') || ''}`
                  }
                  automationIdPrefix="Business_Cash_Advance_Calculation_Slider_How_Much_Do_You_Need"
                />
              </div>
              <div className="column is-narrow">
                <div
                  className="slider-down is-clickable"
                  onClick={() => {
                    if (fundedAmountIndex < amountOptions.length - 1) {
                      const newFundedAmountIndex = fundedAmountIndex + 1;
                      setOfferLoading(true);
                      setFundedAmountIndex(newFundedAmountIndex);
                      updatePeriodSlider(
                        periodOptions[paymentPeriodIndex],
                        amountOptions[newFundedAmountIndex],
                      );
                    }
                  }}
                >
                  <span className="is-icon-5 icon-plus"></span>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-container">
            <h3 className="box-title is-size-6 has-padding-top-7 has-margin-bottom-6">
              Approximate payment period
            </h3>
            <div className="columns is-mobile">
              <div className="column is-narrow">
                <div
                  className="slider-down is-clickable"
                  onClick={() => {
                    if (paymentPeriodIndex > 0) {
                      setOfferLoading(true);
                      setPaymentPeriodIndex(paymentPeriodIndex - 1);
                    }
                  }}
                >
                  <span className="is-icon-5 icon-minus"></span>
                </div>
              </div>
              <div className="column">
                <Slider
                  disabled={periodOptions.length <= 1}
                  xMax={periodOptions.length - 1 || 0}
                  x={paymentPeriodIndex || 0}
                  isMobile={false}
                  onChange={({ x }) => {
                    setOfferLoading(true);
                    setPaymentPeriodIndex(x);
                  }}
                  xLabelFormatter={(x) =>
                    periodOptions[x] && periodOptions.length > 1 ? `${periodOptions[x]} months` : ''
                  }
                  automationIdPrefix="Business_Cash_Advance_Calculation_Slider_Estimated_Payment_Period"
                  singleOption={(x) =>
                    periodOptions.length > 1 ? '' : `${periodOptions[x ?? ''] || '  '} months`
                  }
                />
              </div>
              <div className="column is-narrow">
                <div
                  className="slider-down is-clickable"
                  onClick={() => {
                    if (paymentPeriodIndex < periodOptions.length - 1) {
                      setOfferLoading(true);
                      setPaymentPeriodIndex(paymentPeriodIndex + 1);
                    }
                  }}
                >
                  <span className="is-icon-5 icon-plus"></span>
                </div>
              </div>
            </div>
          </div>

          <h3 className="repayment-factor-rate-title">
            Repayment rate &nbsp;
            <span
              className="bca-tooltip"
              data-tip="React-tooltip"
              automationid="Business_Cash_Advance_Calculation_Span_Repayment_Factor_Rate_Help_Hover"
              onTouchStart={() => TealiumEvents.applyNowBCAClickOnPaymentRefactorInfoButton('')}
              onMouseEnter={() => TealiumEvents.applyNowBCAClickOnPaymentRefactorInfoButton('')}
            >
              i
            </span>
          </h3>
          <ReactTooltip
            place="bottom"
            type="light"
            effect="solid"
            aria-haspopup="true"
            ref={BCATooltip}
            className="React-tooltip is-hidden-mobile"
          >
            {tooltipContent()}
          </ReactTooltip>
          <ReactTooltip
            place="bottom"
            type="light"
            effect="solid"
            aria-haspopup="true"
            clickable={true}
            event="click"
            globalEventOff="click"
            ref={BCATooltip}
            className="React-tooltip is-hidden-tablet"
          >
            {tooltipContent()}
          </ReactTooltip>
          <div className="columns payment-period-container">
            <div
              className="column payment-period"
              automationid="Business_Cash_Advance_Calculation_Div_Repayment_Factor_Rate_Percentage"
            >
              {!offerLoading ? (
                offerAvailable() ? (
                  `${currentOffer?.Rate}%`
                ) : (
                  'No data'
                )
              ) : (
                <StaticLoader />
              )}
              <span
                className="bca-tooltip is-hidden-tablet"
                data-tip="React-tooltip"
                automationid="Business_Cash_Advance_Calculation_Span_Repayment_Factor_Rate_Help_Hover"
                onTouchStart={() => TealiumEvents.applyNowBCAClickOnPaymentRefactorInfoButton('')}
              >
                i
              </span>
            </div>
            <div className="column is-narrow columns is-mobile is-gapless payment-total">
              <span className="column has-text-left has-margin-right-4">
                Total Repayment
                <span className="is-hidden-tablet">:</span>
              </span>
              <span className="column is-narrow has-text-right">
                {!offerLoading ? (
                  offerAvailable() ? (
                    `R ${formatCurrencyAmountSimple(currentOffer?.TotalRepayment)}`
                  ) : (
                    'No data'
                  )
                ) : (
                  <StaticLoader />
                )}
              </span>
            </div>
          </div>
          <p>
            Daily repayment amount will be {currentOffer?.Rate ?? ''}% of your daily turnover
            processed through all your point of sale terminals.
            <br /> * QR Code, POS Device, VPS Payment Gateway <br /> * Repayment period is estimated
            based on your predicted turnover. The period may be extended or shortened as the advance
            is repaid.
          </p>
        </Card>

        <Card cardTitle="Application summary" className="application-summary">
          <table className="table application-summary-table is-fullwidth">
            <tbody>
              <tr>
                <td>
                  Funding amount<span className="is-hidden-tablet">:</span>
                </td>
                <td className="has-text-right" automationid="BCA_Div_Funding_Amount">
                  {!offerLoading ? (
                    offerAvailable() ? (
                      `R ${formatCurrencyAmountSimple(currentOffer?.Amount)}`
                    ) : (
                      'No data'
                    )
                  ) : (
                    <StaticLoader />
                  )}
                </td>
              </tr>
              <tr>
                <td>
                  Repayment frequency<span className="is-hidden-tablet">:</span>
                </td>
                <td className="has-text-right" automationid="BCA_Div_Repayment_Frequency">
                  Daily
                </td>
              </tr>
              <tr>
                <td>
                  Approximate repayment term<span className="is-hidden-tablet">:</span>
                </td>
                <td className="has-text-right" automationid="BCA_Div_Repayment_Term">
                  {!offerLoading ? (
                    offerAvailable() ? (
                      currentOffer?.Term + ' Months'
                    ) : (
                      '--- Months'
                    )
                  ) : (
                    <StaticLoader />
                  )}
                </td>
              </tr>
              <tr>
                <td>
                  Cost of funding<span className="is-hidden-tablet">:</span>
                </td>
                <td className="has-text-right" automationid="BCA_Div_Repayment_Fixed_Fee">
                  {/* Ideally should not be calculated, but we make do with what we get */}
                  {!offerLoading ? (
                    offerAvailable() ? (
                      `R ${formatCurrencyAmountSimple(
                        (currentOffer?.TotalRepayment ?? 0) - (currentOffer?.Amount ?? 0),
                      )}`
                    ) : (
                      'No data'
                    )
                  ) : (
                    <StaticLoader />
                  )}
                </td>
              </tr>
              <tr>
                <td>
                  Total repayment<span className="is-hidden-tablet">:</span>
                </td>
                <td className="has-text-right" automationid="BCA_Div_Repayment_Total">
                  {!offerLoading ? (
                    offerAvailable() ? (
                      `R ${formatCurrencyAmountSimple(currentOffer?.TotalRepayment)}`
                    ) : (
                      'No data'
                    )
                  ) : (
                    <StaticLoader />
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </Card>
        <div className="get-advance-container is-hidden-mobile">
          <div className="get-advance">
            <button
              automationid="BCA_Button_Get_Advance"
              className="button is-primary is-fullwidth"
              onClick={() => {
                const amount = currentOffer?.Amount || -1;
                const term = currentOffer?.Term ?? 0;

                TealiumEvents.applyNowBCAClickOnGetAdvance(term.toString(), amount.toString());

                setShowAdditionalFieldsModal(true);
              }}
              disabled={offerLoading || !offerAvailable()}
            >
              Get Advance
            </button>
          </div>
        </div>
        <div className="is-hidden-tablet mobile-get-advance-button">
          <button
            automationid="BCA_Button_Get_Advance"
            className="button is-primary is-fullwidth"
            onClick={() => {
              const amount = currentOffer?.Amount || -1;
              const term = currentOffer?.Term ?? 0;

              TealiumEvents.applyNowBCAClickOnGetAdvance(term.toString(), amount.toString());

              setShowAdditionalFieldsModal(true);
            }}
          >
            Get advance
          </button>
        </div>
      </HeaderNav>
    </div>
  );
}
